// The contact filter the UI builds and the server runs (business.ContactFilter
// in Go): groups of terms, the terms in a group joined by the group's op, the
// groups joined by the filter's op. Pure (no React), so it's unit-tested.
//
//   { op: "and"|"or", groups: [ { op: "and"|"or", terms: [ term, ... ] } ] }

export const AND = "and";
export const OR = "or";

export const emptyFilter = () => ({ op: AND, groups: [{ op: AND, terms: [] }] });

// ---------- the fields a term can use ----------

const ID_FIELDS = {
  tag: {
    label: "Tag",
    ops: [["any", "has any of"], ["all", "has all of"], ["none", "has none of"]],
    source: "tags",
  },
  list: {
    label: "List",
    ops: [["on", "is on"], ["not_on", "is not on"], ["no_list", "is on no list"]],
    source: "lists",
    statuses: [["", "any status"], ["active", "active"], ["pending", "pending"], ["unsubscribed", "unsubscribed"]],
  },
  signup: {
    label: "Signed up through",
    ops: [["any", "any of"], ["none", "none of"]],
    source: "campaigns",
  },
  product: {
    label: "Product",
    ops: [["any", "signed up for any of"], ["none", "didn't sign up for"]],
    source: "products",
  },
  sequence: {
    label: "Sequence",
    ops: [["in", "is in"], ["not_in", "is not in"]],
    source: "sequences",
    statuses: [["", "any status"], ["active", "active"], ["completed", "completed"], ["cancelled", "cancelled"]],
  },
};

export const STATUS_VALUES = [
  ["active", "Active"],
  ["bounced", "Bounced"],
  ["complained", "Complained"],
  ["unsubscribed", "Unsubscribed"],
];

export const TEXT_OPS = [
  ["contains", "contains"],
  ["not_contains", "doesn't contain"],
  ["is", "is"],
  ["is_not", "is not"],
  ["empty", "is empty"],
  ["not_empty", "is not empty"],
];

export const NUMBER_OPS = [
  ["eq", "is"],
  ["gt", "is more than"],
  ["gte", "is at least"],
  ["lt", "is less than"],
  ["lte", "is at most"],
  ["between", "is between"],
];

export const BOOL_OPS = [["is_true", "is yes"], ["not_true", "is not yes"]];
export const SELECT_OPS = [["any", "is any of"], ["none", "is none of"], ["empty", "is empty"], ["not_empty", "is not empty"]];

// The standard fields, in the order the "Add a rule" menu shows them.
export const FIELDS = [
  { field: "tag", kind: "ids", ...ID_FIELDS.tag },
  { field: "list", kind: "ids", ...ID_FIELDS.list },
  { field: "status", kind: "status", label: "Status", ops: [["is", "is"], ["is_not", "is not"]] },
  { field: "signup", kind: "ids", ...ID_FIELDS.signup },
  { field: "product", kind: "ids", ...ID_FIELDS.product },
  { field: "sequence", kind: "ids", ...ID_FIELDS.sequence },
  { field: "signed_up", kind: "date", label: "Signed up" },
  { field: "added", kind: "date", label: "Added" },
  { field: "confirmed", kind: "date", label: "Confirmed" },
  { field: "last_opened", kind: "date", label: "Last opened" },
  { field: "emails_sent", kind: "number", label: "Emails sent", ops: NUMBER_OPS },
  { field: "name", kind: "text", label: "Name", ops: TEXT_OPS },
  { field: "email", kind: "text", label: "Email", ops: TEXT_OPS },
  { field: "city", kind: "text", label: "City", ops: TEXT_OPS },
  { field: "state", kind: "text", label: "State", ops: TEXT_OPS },
  { field: "country", kind: "text", label: "Country", ops: TEXT_OPS },
  { field: "distance", kind: "distance", label: "Distance", ops: [["within", "within"], ["beyond", "farther than"]] },
];

export const DISTANCE_UNITS = [["mi", "miles"], ["km", "km"]];

const TEXT_TYPES = ["text", "email", "phone", "zip", "city", "state", "country"];

// fieldOf describes a term's field: a standard one, or a custom field (by the
// org's definitions). Null when unknown (e.g. a deleted custom field).
export function fieldOf(term, customFieldDefs = []) {
  if (term.field !== "cf") return FIELDS.find((f) => f.field === term.field) || null;
  const def = customFieldDefs.find((d) => d.key === term.key);
  const type = term.field_type || (def && def.field_type);
  const label = (def && (def.label || def.key)) || term.key;
  if (TEXT_TYPES.includes(type)) return { field: "cf", kind: "text", label, ops: TEXT_OPS, type };
  if (type === "number") return { field: "cf", kind: "number", label, ops: [...NUMBER_OPS, ["empty", "is empty"], ["not_empty", "is not empty"]], type };
  if (type === "date") return { field: "cf", kind: "date", label, type };
  if (type === "boolean") return { field: "cf", kind: "bool", label, ops: BOOL_OPS, type };
  if (type === "select") return { field: "cf", kind: "select", label, ops: SELECT_OPS, type, options: (def && def.options) || [] };
  return null;
}

// customFields lists the custom fields a filter can use (archived ones and
// unknown types left out), as "Add a rule" menu entries.
export function customFields(defs = []) {
  return defs
    .filter((d) => !d.archived)
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .map((d) => ({ def: d, info: fieldOf({ field: "cf", key: d.key, field_type: d.field_type }, defs) }))
    .filter((x) => x.info);
}

// newTerm starts a term for a field, with its first op picked.
export function newTerm(field, def) {
  if (field === "cf") {
    const info = fieldOf({ field: "cf", key: def.key, field_type: def.field_type }, [def]);
    const term = { field: "cf", key: def.key, field_type: def.field_type };
    if (info.kind === "date") return { ...term, date: null };
    return { ...term, op: info.ops[0][0] };
  }
  const info = FIELDS.find((f) => f.field === field);
  if (info.kind === "date") return { field, date: null };
  if (info.kind === "distance") return { field, op: "within", number: 50, unit: "mi", place: null };
  return { field, op: info.ops[0][0] };
}

// ---------- when is a term ready to run ----------

const noValueOps = ["empty", "not_empty", "no_list", "is_true", "not_true"];

export function isTermComplete(term, customFieldDefs = []) {
  const info = fieldOf(term, customFieldDefs);
  if (!info) return false;
  if (info.kind === "date") return isDateComplete(term.date);
  if (noValueOps.includes(term.op)) return true;
  switch (info.kind) {
    case "ids":
      return (term.ids || []).length + (term.form_ids || []).length > 0;
    case "status":
    case "select":
      return (term.values || []).length > 0;
    case "number":
      return term.number != null && (term.op !== "between" || term.number2 != null);
    case "text":
      return !!(term.text || "").trim();
    case "distance":
      return term.number > 0 && !!term.place;
    default:
      return false;
  }
}

export function isDateComplete(d) {
  if (!d || !d.op) return false;
  if (d.op === "exists" || d.op === "not_exists") return true;
  if (d.op === "in_last" || d.op === "not_in_last") return !!d.period || d.days >= 1;
  if (d.op === "between" || d.op === "not_between") return !!d.from && !!d.to && d.from <= d.to;
  return false;
}

// forServer drops unfinished terms and empty groups — what's sent to count or
// search while someone is still building. Null when nothing's left.
export function forServer(filter, customFieldDefs = []) {
  if (!filter) return null;
  const groups = (filter.groups || [])
    .map((g) => ({ op: g.op || AND, terms: (g.terms || []).filter((t) => isTermComplete(t, customFieldDefs)) }))
    .filter((g) => g.terms.length > 0);
  return groups.length ? { op: filter.op || AND, groups } : null;
}

export const termCount = (filter) => (filter ? filter.groups.reduce((n, g) => n + g.terms.length, 0) : 0);

// ---------- dates ----------

export const DATE_OPS = [
  ["exists", "has a date"],
  ["not_exists", "has no date"],
  ["in_last", "in the last"],
  ["not_in_last", "not in the last"],
  ["between", "between"],
  ["not_between", "not between"],
];

// Presets under the "[N] days" box: rolling spans set days, calendar ones
// ("this …") set a period.
export const DATE_PRESETS = [
  { label: "Last week", days: 7 },
  { label: "Last 2 weeks", days: 14 },
  { label: "Last month", days: 30 },
  { label: "Last quarter", days: 90 },
  { label: "Last year", days: 365 },
  { label: "This week", period: "this_week" },
  { label: "This month", period: "this_month" },
  { label: "This quarter", period: "this_quarter" },
  { label: "This year", period: "this_year" },
];

const utcDay = (y, m, d) => new Date(Date.UTC(y, m, d));

// resolveDateRange mirrors the server's ResolveDateRange (UTC; weeks start
// Monday): { from, to } as Dates, to = null for "until now". Null for
// exists / not_exists or an unfinished condition.
export function resolveDateRange(d, now = new Date()) {
  if (!isDateComplete(d)) return null;
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  if (d.op === "in_last" || d.op === "not_in_last") {
    let from;
    switch (d.period) {
      case "this_week": {
        const dow = (now.getUTCDay() + 6) % 7; // Monday = 0
        from = utcDay(y, m, now.getUTCDate() - dow);
        break;
      }
      case "this_month": from = utcDay(y, m, 1); break;
      case "this_quarter": from = utcDay(y, Math.floor(m / 3) * 3, 1); break;
      case "this_year": from = utcDay(y, 0, 1); break;
      default: from = new Date(now.getTime() - d.days * 86400000);
    }
    return { from, to: null };
  }
  if (d.op === "between" || d.op === "not_between") {
    const [fy, fm, fd] = d.from.split("-").map(Number);
    const [ty, tm, td] = d.to.split("-").map(Number);
    return { from: utcDay(fy, fm - 1, fd), to: utcDay(ty, tm - 1, td + 1) };
  }
  return null;
}

const fmtDay = (date) =>
  date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });

// describeDateRange is the resolved range in words, shown under the picker:
// "Since Sep 14, 2026" or "Sep 1, 2026 – Sep 10, 2026".
export function describeDateRange(d, now = new Date()) {
  const r = resolveDateRange(d, now);
  if (!r) return "";
  if (!r.to) return "Since " + fmtDay(r.from);
  const last = new Date(r.to.getTime() - 86400000);
  return r.from.getTime() === last.getTime() ? fmtDay(r.from) : fmtDay(r.from) + " – " + fmtDay(last);
}

// dateText is a date condition in words, for chips: "in the last 30 days",
// "this month", "not between Sep 1, 2026 and Sep 10, 2026".
export function dateText(d) {
  if (!isDateComplete(d)) return "…";
  const preset = DATE_PRESETS.find((p) => (d.period ? p.period === d.period : false));
  switch (d.op) {
    case "exists": return "has a date";
    case "not_exists": return "has no date";
    case "in_last":
      return preset ? preset.label.toLowerCase() : `in the last ${d.days} day${d.days === 1 ? "" : "s"}`;
    case "not_in_last":
      return preset ? `not ${preset.label.toLowerCase()}` : `not in the last ${d.days} day${d.days === 1 ? "" : "s"}`;
    default: {
      const r = resolveDateRange(d);
      const last = new Date(r.to.getTime() - 86400000);
      return `${d.op === "between" ? "between" : "not between"} ${fmtDay(r.from)} and ${fmtDay(last)}`;
    }
  }
}

// ---------- terms in words (chips, segment summaries) ----------

const opLabel = (ops, op) => ((ops || []).find(([v]) => v === op) || [op, op])[1];

// names is { tags, lists, campaigns, forms, products, sequences }: id → name.
export function termText(term, names = {}, customFieldDefs = []) {
  const info = fieldOf(term, customFieldDefs);
  if (!info) return "Unknown rule";
  const nameOf = (source, id) => ((names[source] || {})[id]) || "(deleted)";
  const list = (source, ids) => (ids || []).map((id) => nameOf(source, id)).join(", ");
  const op = opLabel(info.ops, term.op);
  switch (info.kind) {
    case "ids": {
      if (term.op === "no_list") return "On no list";
      let what = list(info.source, term.ids);
      if (term.field === "signup" && (term.form_ids || []).length) {
        what = [what, list("forms", term.form_ids)].filter(Boolean).join(", ");
      }
      const status = term.status ? " (" + opLabel(info.statuses, term.status) + ")" : "";
      return `${info.label} ${op} ${what}${status}`;
    }
    case "status":
      return `Status ${op} ${(term.values || []).map((v) => opLabel(STATUS_VALUES, v)).join(" or ")}`;
    case "date":
      return `${info.label} ${dateText(term.date)}`;
    case "number":
      if (term.op === "between") return `${info.label} is between ${term.number} and ${term.number2}`;
      if (noValueOps.includes(term.op)) return `${info.label} ${op}`;
      return `${info.label} ${op} ${term.number}`;
    case "text":
      return noValueOps.includes(term.op) ? `${info.label} ${op}` : `${info.label} ${op} "${term.text}"`;
    case "bool":
      return `${info.label} ${op}`;
    case "distance": {
      const unit = opLabel(DISTANCE_UNITS, term.unit);
      const place = term.place ? term.place.name : "…";
      return `${term.op === "beyond" ? "Farther than" : "Within"} ${term.number} ${unit} of ${place}`;
    }
    case "select":
      return noValueOps.includes(term.op) ? `${info.label} ${op}` : `${info.label} ${op} ${(term.values || []).join(", ")}`;
    default:
      return info.label;
  }
}

// filterText is the whole filter in words: groups in brackets when there's
// more than one, joined by their op.
export function filterText(filter, names = {}, customFieldDefs = []) {
  const f = filter && filter.groups ? filter : null;
  if (!f || termCount(f) === 0) return "";
  const groups = f.groups.filter((g) => g.terms.length);
  const word = (op) => (op === OR ? " or " : " and ");
  const parts = groups.map((g) => {
    const text = g.terms.map((t) => termText(t, names, customFieldDefs)).join(word(g.op));
    return groups.length > 1 && g.terms.length > 1 ? `(${text})` : text;
  });
  return parts.join(word(f.op).toUpperCase());
}

// ---------- editing ----------

// updateTerm / removeTerm return a new filter with one term changed or gone
// (a group left empty is dropped, but the filter always keeps one group).
export function updateTerm(filter, gi, ti, term) {
  return {
    ...filter,
    groups: filter.groups.map((g, i) => (i !== gi ? g : { ...g, terms: g.terms.map((t, j) => (j === ti ? term : t)) })),
  };
}

export function removeTerm(filter, gi, ti) {
  const groups = filter.groups
    .map((g, i) => (i !== gi ? g : { ...g, terms: g.terms.filter((_, j) => j !== ti) }))
    .filter((g, i) => g.terms.length > 0 || i === 0);
  return { ...filter, groups: groups.length ? groups : [{ op: AND, terms: [] }] };
}

export function addTerm(filter, gi, term) {
  return { ...filter, groups: filter.groups.map((g, i) => (i === gi ? { ...g, terms: [...g.terms, term] } : g)) };
}

// ---------- URLs and old links ----------

export function filterToParam(filter) {
  return filter && termCount(filter) ? JSON.stringify(filter) : "";
}

export function filterFromParam(value) {
  if (!value) return null;
  try {
    const f = JSON.parse(value);
    return f && Array.isArray(f.groups) ? f : null;
  } catch (e) {
    return null;
  }
}

// fromLegacy turns the old list / status / tag filters (old links, older
// segments) into a filter. "all" or "" = no list filter; "None" = on no list;
// "suppressed" = any non-active status.
export function fromLegacy({ listId, status, tagId } = {}) {
  const terms = [];
  if (listId === "None") terms.push({ field: "list", op: "no_list" });
  else if (listId && listId !== "all") terms.push({ field: "list", op: "on", ids: [listId] });
  if (status === "suppressed") terms.push({ field: "status", op: "is", values: ["bounced", "complained", "unsubscribed"] });
  else if (status) terms.push({ field: "status", op: "is", values: [status] });
  if (tagId) terms.push({ field: "tag", op: "any", ids: [tagId] });
  return terms.length ? { op: AND, groups: [{ op: AND, terms }] } : null;
}

// requiredList is the list every match must be on, when the filter makes that
// certain (a lone "is on <list>" rule every result has to meet), so the table
// can show when each contact joined that list. "all" otherwise.
export function requiredList(filter) {
  const f = forServer(filter);
  if (!f || (f.op === OR && f.groups.length > 1)) return "all";
  for (const g of f.groups) {
    if (g.op === OR && g.terms.length > 1) continue;
    const t = g.terms.find((x) => x.field === "list" && x.op === "on" && (x.ids || []).length === 1 && !x.status);
    if (t) return t.ids[0];
  }
  return "all";
}
